import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { ArrowRightLeft, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';

interface RolloutRequestFormProps {
  workspaceId: string;
  projectId?: string;
  onSuccess?: () => void;
}

export function RolloutRequestForm({ workspaceId, projectId, onSuccess }: RolloutRequestFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    account_id: '',
    rollout_date: '',
    reason: '',
    stop_bill_feeding: true,
    archive_bills: false,
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, account_number, sites!inner(name, customers!inner(name))')
        .eq('workspace_id', workspaceId)
        .eq('status', 'active')
        .order('account_number');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspaceId,
  });

  const createRequestMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const requestData = {
        account_id: formData.account_id,
        rollout_date: formData.rollout_date,
        reason: formData.reason,
        stop_bill_feeding: formData.stop_bill_feeding,
        archive_bills: formData.archive_bills,
      };

      const { data, error } = await supabase
        .from('tum_change_management_requests')
        .insert({
          workspace_id: workspaceId,
          onboarding_project_id: projectId || null,
          request_type: 'rollout',
          request_data: requestData,
          description: formData.description,
          priority: formData.priority,
          requested_effective_date: formData.rollout_date || null,
          requested_by: user.id,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-management-counts', workspaceId] });
      toast({
        title: 'Rollout request submitted',
        description: 'Your request has been submitted and is pending approval.',
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error submitting request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.account_id) {
      toast({
        title: 'Account required',
        description: 'Please select an account',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.rollout_date) {
      toast({
        title: 'Rollout date required',
        description: 'Please select a rollout date',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.reason.trim()) {
      toast({
        title: 'Reason required',
        description: 'Please provide a reason for the rollout',
        variant: 'destructive',
      });
      return;
    }

    createRequestMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowRightLeft className="h-5 w-5" />
          Rollout Request
        </CardTitle>
        <CardDescription>
          Stop feeding bill entries for an existing account. This will prevent new bills from being processed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="account">Account *</Label>
            <Select
              value={formData.account_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, account_id: value }))}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select account to rollout" />
              </SelectTrigger>
              <SelectContent>
                {accounts?.map((account: any) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.account_number} - {account.sites?.name || 'Unknown Site'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rolloutDate">Rollout Date *</Label>
            <Input
              id="rolloutDate"
              type="date"
              value={formData.rollout_date}
              onChange={(e) => setFormData(prev => ({ ...prev, rollout_date: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason *</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Explain why this account should be rolled out..."
              required
              rows={3}
            />
          </div>

          <div className="space-y-4 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="stopBills">Stop Bill Feeding</Label>
                <p className="text-xs text-muted-foreground">
                  Prevent new bills from being processed for this account
                </p>
              </div>
              <Switch
                id="stopBills"
                checked={formData.stop_bill_feeding}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, stop_bill_feeding: checked }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="archiveBills">Archive Existing Bills</Label>
                <p className="text-xs text-muted-foreground">
                  Archive all existing bills for this account
                </p>
              </div>
              <Switch
                id="archiveBills"
                checked={formData.archive_bills}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, archive_bills: checked }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Additional Notes</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Any additional information..."
              rows={3}
            />
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This request will stop bill processing for the selected account. Make sure this is the correct action.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button 
              type="submit"
              disabled={createRequestMutation.isPending}
              className="flex-1"
            >
              {createRequestMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

